// Pure, framework-free helpers for PhotoOptionsForm's multi-select state.
//
// These are extracted so the zero-selection / Save-disabled behaviour can be
// asserted by a fast unit test that fails loudly, instead of relying solely on
// the E2E fiber-walk in `e2e/multi-mode.spec.js` (which silently skips if
// React's internal fiber key prefix changes).

/**
 * Save must be disabled while a submit is in flight, whenever either the
 * capture-mode or image-style selection is empty (at least one of each is
 * required for a valid channel), or while the BIPA consent acknowledgment
 * blocks it (see consentAckBlocksSave).
 */
export const isSaveDisabled = ({
  isSubmitting,
  enabledModeIds,
  enabledImageStylesUi,
  consentBlocked = false,
}) =>
  Boolean(isSubmitting) ||
  Boolean(consentBlocked) ||
  (enabledModeIds || []).length === 0 ||
  (enabledImageStylesUi || []).length === 0;

/**
 * Why the Photos tab's Save is off because of the selection, in plain words, or
 * null when the selection is fine. The BIPA consent block explains itself in
 * its own notice, and an in-flight submit needs no reason.
 */
export const saveBlockReason = ({ enabledModeIds, enabledImageStylesUi }) =>
  (enabledModeIds || []).length === 0 || (enabledImageStylesUi || []).length === 0
    ? "Pick at least one capture mode and one image style."
    : null;

// === BIPA consent acknowledgment gating ========================================
//
// Enabling the AI capture mode turns on biometric processing for a channel's
// guests, so the operator must acknowledge the consent requirement before the
// channel can be SAVED with AI newly enabled. Deliberately client-side only:
// the hard legal gate is rails' 422 on AI-headshot creation without a guest's
// written consent; this acknowledgment is the operator-facing half. A channel
// that ALREADY has AI persisted is never re-gated — the acknowledgment was
// given when it was enabled, and re-demanding it on every unrelated save of
// the Photos tab would train operators to click through it.

/** Whether the given selected capture-mode shortnames include the AI mode. */
export const aiModeSelected = (selectedShortnames) =>
  (selectedShortnames || []).includes("ai");

/**
 * Whether the unacknowledged consent requirement blocks Save: AI mode is
 * selected, was NOT already persisted on the channel, and the operator has not
 * checked the acknowledgment.
 */
export const consentAckBlocksSave = ({ aiSelected, aiPersisted, acked }) =>
  Boolean(aiSelected) && !aiPersisted && !acked;

/**
 * The capture-mode ids the channel has PERSISTED, read from the server-derived
 * `initialValues` that seed the edit form (EditChannel's `initialChannel`) —
 * deliberately NOT from live form state, which reflects unsaved edits. Deriving
 * "already persisted" from form values would let any remount of the Photos tab
 * (a picker-variant flip when the multi-select flag resolves late, a tab
 * switch and back) mistake an unsaved AI selection for a persisted one and
 * skip the acknowledgment. A new channel has no initialValues → nothing
 * persisted. Both the legacy scalar `mode_id` and the multi-select
 * `enabled_mode_ids` are honored, whichever the channel was saved with.
 */
export const persistedModeIds = (initialValues) => {
  if (!initialValues) return [];
  const ids = Array.isArray(initialValues.enabled_mode_ids)
    ? [...initialValues.enabled_mode_ids]
    : [];
  if (initialValues.mode_id != null) ids.push(initialValues.mode_id);
  return ids;
};

/** Whether the given selected capture-mode shortnames include Story Video. */
export const videoModeSelected = (selectedShortnames) =>
  (selectedShortnames || []).includes("video");

/**
 * Whether the signed-in user OWNS the channel the form is editing. Rails'
 * story-video write gate keys on the channel OWNER's `story_video_enabled`
 * flag, while the client's PostHog flag is the SIGNED-IN user's, so the two
 * only agree when the editor is the owner. A new channel (no server id yet) is
 * created with `owner: current_user`. On edit, the v2 detail read's `can_delete`
 * is ChannelPolicy#destroy? (owner OR super_admin), the server's ownership
 * verdict; anything else, including a missing field, is not-owner (fail
 * closed: a collaborator is never offered a selection rails would 422).
 */
export const ownsChannel = (initialValues) =>
  !initialValues || initialValues.id == null
    ? true
    : initialValues.can_delete === true;

/**
 * The capture-mode ids a SUBMIT of the shared channel form would persist,
 * read from LIVE form values (the counterpart of `persistedModeIds`, which
 * reads server-seeded initialValues). The union of the multi-select array and
 * the legacy scalar is deliberate, fail-closed: whichever field(s) the active
 * picker variant maintains, an AI id present in either would be persisted by
 * rails, so either must trip the consent gate.
 */
export const submittedModeIds = (values) => {
  if (!values) return [];
  const ids = Array.isArray(values.enabled_mode_ids)
    ? [...values.enabled_mode_ids]
    : [];
  if (values.mode_id != null && !ids.includes(values.mode_id)) {
    ids.push(values.mode_id);
  }
  return ids;
};

/**
 * Helper copy shown under each section, reflecting how many options are
 * selected and nudging the user toward a valid (>= 1) selection.
 */
export const sectionHelperText = (count) =>
  count > 1
    ? 'Multiple selected: guests will choose at the booth.'
    : count === 1
    ? '1 selected: capture starts immediately.'
    : 'Select at least one to enable Save.';

// === Mode → image-style filtering =============================================
//
// A capture mode constrains which image styles are offered: the classic modes
// (4pose/tnb/still) offer the standard/bnw/glitch styles; the AI mode offers
// only the Professional Headshot style. This is expressed declaratively — each
// image-style definition carries a `modes` array naming the capture-mode
// shortnames it belongs to — so adding a future AI style is one array entry,
// not a new branch. These helpers are pure + framework-free so the intersection
// logic is unit-tested directly rather than only through a form render.

/**
 * Resolve the selected capture-mode SHORTNAMES from the selected numeric mode
 * ids. `captureModes` is the array of mode definitions (each exposes
 * `.shortname` and `.value(modes)` → the resolved id); `modes` is the `/v2`
 * catalog; `selectedIds` is the currently-selected mode id(s). Ids are resolved
 * through the definitions themselves so the mapping holds whether or not the
 * catalog has loaded (the definitions fall back to their hardcoded ids).
 */
export const selectedModeShortnames = (captureModes, modes, selectedIds) => {
  const idToShortname = {};
  (captureModes || []).forEach((mode) => {
    idToShortname[String(mode.value(modes))] = mode.shortname;
  });
  return (selectedIds || [])
    .map((id) => idToShortname[String(id)])
    .filter(Boolean);
};

/**
 * Filter image-style definitions to those whose `modes` intersect the given
 * selected capture-mode shortnames. A style with no `modes` array is treated as
 * universal (defensive — every shipped style declares its modes).
 */
export const visibleImageStyles = (imageStyles, selectedShortnames) =>
  (imageStyles || []).filter(
    (style) =>
      !Array.isArray(style.modes) ||
      style.modes.some((m) => (selectedShortnames || []).includes(m))
  );

// === Legacy single-select image-style value <-> customization mapping =========
//
// The legacy (flag-off) form stores the image style as two booleans on
// `channel_image_customization`, which can only express standard/bnw/glitch.
// The AI headshot style has no boolean slot, so it rides the multi-select wire
// field `enabled_image_styles` (the same field rails reads for the token). The
// two helpers below are the single source of truth for reading a style value
// out of, and writing a chosen style back into, the legacy customization shape.

/** The wire token for the AI Professional Headshot image style. */
export const HEADSHOT_STYLE_VALUE = 'professional_headshot';

/**
 * Derive the currently-selected style value
 * ("standard"|"bnw"|"glitch"|"professional_headshot") from a legacy
 * single-select customization object (or "" when there is no customization).
 */
export const singleSelectStyleValue = (customization) => {
  if (!customization) return '';
  if (
    Array.isArray(customization.enabled_image_styles) &&
    customization.enabled_image_styles.includes(HEADSHOT_STYLE_VALUE)
  ) {
    return HEADSHOT_STYLE_VALUE;
  }
  if (customization.is_black_and_white_mode_enabled) return 'bnw';
  if (customization.is_glitch_mode_enabled) return 'glitch';
  return 'standard';
};

/** The wire token for the B&W style; the form's UI vocabulary says "bnw". */
export const WIRE_STYLE_BW = 'bw';
export const UI_STYLE_BNW = 'bnw';
export const mapUiStyleToWire = (uiToken) =>
  uiToken === UI_STYLE_BNW ? WIRE_STYLE_BW : uiToken;
export const mapWireStyleToUi = (wireToken) =>
  wireToken === WIRE_STYLE_BW ? UI_STYLE_BNW : wireToken;

/**
 * Build the customization patch for a chosen single-select style value,
 * preserving the existing record id. The patch ALWAYS carries the chosen style
 * as a one-element `enabled_image_styles` array alongside the legacy booleans.
 *
 * Why the array must be written on every selection (prod incident 2026-09-10):
 * the wire serializer (`buildModeAndStyleParams` in utils/api/channels.js)
 * treats a present `enabled_image_styles` array as the source of truth and
 * re-derives the legacy booleans from its first element. An earlier version of
 * this helper left the array untouched for classic styles "to keep the legacy
 * payload byte-identical", so the form kept the server-seeded array and every
 * Standard/B&W/Glitch change was silently overwritten by the stale array on
 * save — a 200 that persisted the old style. Writing both sides here keeps the
 * form state coherent no matter which field the serializer prefers.
 *
 * For the headshot patch the payload is deliberately UNAMBIGUOUS: both legacy
 * booleans are set to `false` AND `enabled_image_styles` carries
 * `['professional_headshot']`. That leaves no doubt which style is intended.
 *
 * CROSS-LANE CONTRACT (rails owns the other half): rails'
 * `reconcile_image_style_state` MUST let a known AI token in the incoming
 * `enabled_image_styles` win over the legacy-boolean derivation, and
 * `ALL_STYLES` must include `professional_headshot`. Otherwise a B&W or
 * Glitch → Headshot transition — which flips a boolean true→false — trips the
 * "booleans changed, booleans win" branch, re-derives `standard` from the now-
 * false booleans, and silently overwrites the sent token (a 200 that persists
 * the wrong style). The react payload cannot avoid this alone: NOT clearing the
 * booleans would instead persist a stale B&W/Glitch flag on a headshot channel.
 * This rails half is shipped in movebooth-rails #1141: the
 * `reconcile_image_style_state` AI-token guard (an incoming
 * `professional_headshot` bypasses the booleans-win branch) plus
 * `professional_headshot` added to `ALL_STYLES`, covered by a B&W-channel →
 * headshot integration test.
 */
export const singleSelectStylePatch = (current, styleValue) => ({
  id: current && current.id,
  is_black_and_white_mode_enabled: styleValue === UI_STYLE_BNW,
  is_glitch_mode_enabled: styleValue === 'glitch',
  enabled_image_styles: [mapUiStyleToWire(styleValue)],
});

/**
 * The form-state patch for a single-select capture-mode choice. Both the
 * legacy scalar `mode_id` AND the multi-select array `enabled_mode_ids` are
 * written, for the same reason `singleSelectStylePatch` writes both style
 * sides: the wire serializer prefers a present `enabled_mode_ids` array, so
 * a radio that only wrote `mode_id` left the server-seeded array in place and
 * every Capture Mode change reverted on save (prod incident 2026-09-10,
 * broken since the 2026-06-23 release).
 */
export const singleSelectModePatch = (modeId) => {
  const id = parseInt(modeId, 10);
  return Number.isNaN(id)
    ? { mode_id: null, enabled_mode_ids: [] }
    : { mode_id: id, enabled_mode_ids: [id] };
};
